import Phaser from 'phaser'
import { topScores, sinceFor } from '@/services/ranking'
import { makeImageTextButton } from '@/ui/button'
import { AudioManager } from '@/services/AudioManager'

type Row = { nickname: string; score: number; stage: number }
type Period = 'daily' | 'weekly'
interface Cream { left: number; right: number; top: number; bottom: number }

export class Leaderboard extends Phaser.Scene {
  private cream!: Cream
  private listTop = 0
  private listContainer?: Phaser.GameObjects.Container
  private status?: Phaser.GameObjects.Text
  private scrollMinY = 0
  private scrollMaxY = 0
  private period: Period = 'daily'
  private tabs: { period: Period; redraw: (active: boolean) => void }[] = []
  private loadToken = 0

  constructor() { super('Leaderboard') }

  create() {
    const { width, height } = this.scale
    this.add.image(width / 2, height / 2, 'bg_lobby').setDisplaySize(width, height)
    AudioManager.init(this)
    AudioManager.startBgm()

    // 패널 프레임(RANKING 배너 내장)
    const panelW = width * 0.94
    const tex = this.textures.get('panel_leaderboard').getSourceImage()
    const panelH = panelW * (tex.height / tex.width)
    const panelTop = height * 0.035
    this.add.image(width / 2, panelTop + panelH / 2, 'panel_leaderboard').setDisplaySize(panelW, panelH)

    // 패널 PNG에서 측정한 실제 크림(내부) 영역 비율
    const cream: Cream = {
      left: width / 2 - panelW * 0.355,
      right: width / 2 + panelW * 0.345,
      top: panelTop + panelH * 0.28,
      bottom: panelTop + panelH * 0.91,
    }
    const creamW = cream.right - cream.left
    const creamH = cream.bottom - cream.top
    // 탭은 크림 위쪽(배너 바로 아래)으로 올리고, 목록은 그 아래 흰 영역 안에서 시작
    const tabY = cream.top - creamH * 0.045
    this.listTop = cream.top + creamH * 0.04
    this.cream = cream

    // 탭: 일간 / 주간 (2개, 중앙 배치)
    const defs: { period: Period; label: string; cx: number }[] = [
      { period: 'daily', label: '일간', cx: cream.left + creamW * 0.3 },
      { period: 'weekly', label: '주간', cx: cream.left + creamW * 0.7 },
    ]
    for (const d of defs) this.makeTab(d.cx, tabY, creamW * 0.3, d.label, d.period)

    this.status = this.add.text((cream.left + cream.right) / 2, (this.listTop + cream.bottom) / 2, '불러오는 중...', {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.045)}px`, color: '#7a5a2a', fontStyle: 'bold',
    }).setOrigin(0.5)

    makeImageTextButton(this, width / 2, panelTop + panelH + (height - panelTop - panelH) * 0.45, 'btn_back', '뒤로',
      () => this.scene.start('Lobby'), { width: width * 0.5, fontSize: width * 0.046 })

    this.registerScroll()
    this.selectPeriod('daily')
  }

  private makeTab(cx: number, cy: number, w: number, label: string, period: Period) {
    const { width } = this.scale
    const h = width * 0.075
    const g = this.add.graphics()
    // 텍스트 색은 생성 시 고정(고정 어두운 갈색) — setColor를 호출하면 텍스처 로드
    // 프레임 타이밍에 Phaser 텍스트 캔버스 재생성이 null drawImage로 크래시한다.
    // 활성/비활성은 배경색 + 알파로만 구분한다(updateText 미발생).
    const txt = this.add.text(cx, cy, label, {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.04)}px`, fontStyle: 'bold', color: '#5a3f12',
    }).setOrigin(0.5)
    const redraw = (active: boolean) => {
      g.clear()
      g.fillStyle(active ? 0xffce6b : 0xffffff, active ? 1 : 0.7)
      g.fillRoundedRect(cx - w / 2, cy - h / 2, w, h, h * 0.4)
      g.lineStyle(Math.max(2, width * 0.005), active ? 0xffffff : 0xc9a06a, 1)
      g.strokeRoundedRect(cx - w / 2, cy - h / 2, w, h, h * 0.4)
      txt.setAlpha(active ? 1 : 0.6)
    }
    redraw(period === this.period)
    const zone = this.add.zone(cx, cy, w, h).setInteractive({ useHandCursor: true })
    zone.on('pointerup', () => { AudioManager.play('ui_click', 0.3); this.selectPeriod(period) })
    this.tabs.push({ period, redraw })
  }

  private selectPeriod(period: Period) {
    this.period = period
    this.tabs.forEach(t => t.redraw(t.period === period))
    void this.loadPeriod(period)
  }

  private async loadPeriod(period: Period) {
    const token = ++this.loadToken
    this.listContainer?.destroy()
    this.listContainer = undefined
    this.status?.setText('불러오는 중...').setVisible(true)
    // 주간은 상위 10위까지만
    const limit = period === 'weekly' ? 10 : 50
    let rows: Row[]
    try {
      // 주간: 닉네임 중복 제거(최고 점수 1개만) → 다양한 사람이 노출. 일간: 중복 허용.
      rows = await topScores(limit, sinceFor(period), period === 'weekly')
    } catch {
      if (!this.scene.isActive() || token !== this.loadToken) return
      this.status?.setText('랭킹을 불러오지 못했어요 😅').setVisible(true)
      return
    }
    if (!this.scene.isActive() || token !== this.loadToken) return
    if (rows.length === 0) {
      const msg = period === 'weekly' ? '이번 주 기록이 없어요 🏆' : '오늘 기록이 없어요 🏆\n첫 도전자가 되어보세요!'
      this.status?.setText(msg).setAlign('center').setVisible(true)
      return
    }
    this.status?.setVisible(false)
    this.buildList(rows)
  }

  private buildList(rows: Row[]) {
    const { width } = this.scale
    const cream = this.cream
    const creamW = cream.right - cream.left
    const listH = cream.bottom - this.listTop
    const rowH = width * 0.108
    const container = this.add.container(0, this.listTop)
    this.listContainer = container

    // 프레임 안에 들어오도록 여백 확보
    const xRank = cream.left + creamW * 0.17
    const xNick = cream.left + creamW * 0.26
    const xScore = cream.right - creamW * 0.13
    const nickFont = Math.round(width * 0.038)
    const scoreFont = Math.round(width * 0.04)
    const maxNickW = xScore - creamW * 0.2 - xNick // 점수와 겹치지 않게

    // 1위=왕관, 2·3·4위=금·은·동 메달, 그 외=숫자
    const iconKey = ['crown', 'medal_gold', 'medal_silver', 'medal_bronze']

    rows.forEach((r, i) => {
      const y = i * rowH + rowH / 2
      if (i % 2 === 1) {
        container.add(this.add.rectangle((cream.left + cream.right) / 2, y, creamW * 0.92, rowH * 0.88, 0x8a5a2a, 0.08))
      }
      const key = iconKey[i]
      if (key) {
        const icon = this.add.image(xRank, y, key).setOrigin(0.5)
        const ih = rowH * (key === 'crown' ? 0.66 : 0.76)
        icon.setDisplaySize(ih * (icon.width / icon.height), ih)
        container.add(icon)
      } else {
        container.add(this.add.text(xRank, y, `${i + 1}`, {
          fontFamily: 'Arial', fontSize: `${Math.round(width * 0.04)}px`, color: '#a4702a', fontStyle: 'bold',
        }).setOrigin(0.5))
      }
      const nickText = this.add.text(xNick, y, r.nickname, {
        fontFamily: 'Arial', fontSize: `${nickFont}px`, color: '#4a3520', fontStyle: 'bold',
      }).setOrigin(0, 0.5)
      this.fitText(nickText, maxNickW)
      container.add(nickText)
      container.add(this.add.text(xScore, y, `${r.score}`, {
        fontFamily: 'Arial', fontSize: `${scoreFont}px`, color: '#d2691e', fontStyle: 'bold',
      }).setOrigin(1, 0.5))
    })

    const maskG = this.add.graphics().setVisible(false)
    maskG.fillStyle(0xffffff)
    maskG.fillRect(cream.left, this.listTop, creamW, listH)
    container.setMask(maskG.createGeometryMask())

    const overflow = Math.max(0, rows.length * rowH - listH)
    this.scrollMaxY = this.listTop
    this.scrollMinY = this.listTop - overflow
    container.y = this.scrollMaxY
  }

  /** 텍스트가 maxW를 넘으면 말줄임(…) 처리 */
  private fitText(t: Phaser.GameObjects.Text, maxW: number) {
    if (t.width <= maxW) return
    const full = t.text
    for (let n = full.length - 1; n >= 1; n--) {
      t.setText(full.slice(0, n) + '…')
      if (t.width <= maxW) return
    }
  }

  // 스크롤 핸들러는 한 번만 등록하고 현재 컨테이너를 참조(탭 전환 시 중복 방지)
  private registerScroll() {
    let dragging = false
    let lastPy = 0
    const inList = (y: number) => y >= this.listTop && y <= this.cream.bottom
    const clamp = (v: number) => Phaser.Math.Clamp(v, this.scrollMinY, this.scrollMaxY)
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (this.listContainer && inList(p.y)) { dragging = true; lastPy = p.y }
    })
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!dragging || !p.isDown || !this.listContainer) return
      this.listContainer.y = clamp(this.listContainer.y + (p.y - lastPy))
      lastPy = p.y
    })
    this.input.on('pointerup', () => { dragging = false })
    this.input.on('wheel', (_p: Phaser.Input.Pointer, _o: unknown, _dx: number, dy: number) => {
      if (this.listContainer) this.listContainer.y = clamp(this.listContainer.y - dy)
    })
  }
}
